<template>
    <Head title="Create Profile" />
    <BaseLayout gap="none" fill-height>
        <Transition name="fade" mode="out-in">
            <LoadingStep v-if="isSubmitting" class="grow" :has-perspective="!!user_basic.has_perspective_report" />
            <ProfileCreationStart
                v-else
                :has-perspective-report="!!user_basic.has_perspective_report"
                :perspective-assessment-enabled="perspective_assessment_enabled"
                :perspective-report-enabled="perspective_report_enabled"
                :document-upload-enabled="document_upload_enabled"
                @create-profile="handleGenerateProfile" />
        </Transition>
        <p v-if="!isGeneratingProfile && generationError" class="body-small text-red-600 text-right mt-2">Failed to generate profile: {{ generationError }}</p>
    </BaseLayout>
</template>

<script lang="ts">
import LeftSidebarLayout from "~vue/layouts/LeftSidebarLayout.vue";
export default { layout: LeftSidebarLayout };
</script>

<script setup lang="ts">
import { Head, router } from "@inertiajs/vue3";
import { useUrlSearchParams } from "@vueuse/core";
import type { UserBasic } from "~vue/appTypes";
import BaseLayout from "~vue/components/BaseLayout.vue";
import LoadingStep from "~vue/components/ProfileWizard/LoadingStep.vue";
import ProfileCreationStart from "~vue/components/ProfileWizard/ProfileCreationStart.vue";
import { useProfileWizard } from "~vue/composables/useProfileWizard";
import { nextTick, ref } from "vue";

export interface Props {
    user_basic: UserBasic;
    perspective_assessment_enabled: boolean;
    perspective_report_enabled: boolean;
    document_upload_enabled: boolean;
}

const { user_basic, perspective_assessment_enabled = false, perspective_report_enabled = false, document_upload_enabled = false } = defineProps<Props>();

// local state to continue showing loading step until the page redirect is complete
const isSubmitting = ref(false);

const { isGeneratingProfile, generateProfile, generationError } = useProfileWizard({
    isManager: !!user_basic.is_manager,
    hasPerspectiveReport: !!user_basic.has_perspective_report,
});

const handleGenerateProfile = async (fileIds?: number[], freeText?: string) => {
    isSubmitting.value = true;
    await generateProfile({ fileIds: fileIds ?? [], freeText, isRecreating: false });
    if (generationError.value) {
        isSubmitting.value = false;
        return;
    }
    router.visit("/accounts/collaboration-profile");
};

const searchParams = useUrlSearchParams("history");
const autoGenerate = searchParams.auto_generate === "true";

if (autoGenerate) {
    void handleGenerateProfile();
    nextTick(() => {
        delete searchParams.auto_generate;
    });
}
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
    transition: opacity 300ms ease-in-out;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}
</style>
